import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Divider,
  FormControlLabel,
  Paper,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { taskFormsApi } from '../../axios';
import { useLanguage } from '../../hooks';
import { datascopeTracker } from '../../utils/eventTracker';
import { Ckeditor } from '../uiComponents/ckeditor';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';
import { MultiEmailInput } from '../uiComponents/multiEmailInput';
import FormEmailBuilderBar from './FormEmailBuilderBar';

const customStyles: Record<string, SxProps<Theme>> = {
  paper: {
    margin: '20px',
    marginBottom: '100px',
    padding: '26px',
    borderRadius: '8px',
  },
  senderAndSubjectContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '18px',
    gap: '32px',
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      gap: '10px',
    },
  },
  textInputDiv: {
    marginTop: '18px',
    width: '-webkit-fill-available',
  },
  textInput: {
    width: '-webkit-fill-available',
    marginTop: '4px',
    '& .MuiTextField-root': {
      marginTop: '5px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '8px',
    },
  },
  divider: {
    margin: '26px 0',
  },
  emailContentContainer: {
    margin: '34px 0 26px 0',
  },
  notificationsConfigContainer: {
    margin: '24px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  checkboxWithLabel: {
    marginLeft: -'9px',
  },
  footerTitle: {
    margin: '20px 0 12px 0px',
  },
  helpIcon: {
    marginLeft: '7px',
    fontSize: '14px',
    cursor: 'help',
  },
  pdfLinkNameTitle: {
    display: 'flex',
    alignItems: 'center',
  },
};

export function FormEmailBuilder() {
  const language = useLanguage();
  const { formId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [subject, setSubject] = useState('');
  const [senderName, setSenderName] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [emailFooter, setEmailFooter] = useState('');
  const [enableFooter, setEnableFooter] = useState(false);
  const [autoNotify, setAutoNotify] = useState(false);
  const [emailsToNotify, setEmailsToNotify] = useState<string[]>([]);
  const [linkName, setLinkName] = useState('');

  const handleConfirm = () => {
    const emailRequestBody = {
      email_subject: subject,
      email_sender_name: senderName,
      email_content: emailContent,
      email_footer: emailFooter,
      auto_notify: autoNotify,
      emails_to_notify: emailsToNotify.join(','),
      email_button_name: linkName,
    };

    taskFormsApi
      .updateTaskFormEmailInfo({ formId: parseInt(formId), emailRequestBody })
      .then(() => {
        enqueueSnackbar(language.formEmailBuilder.saveSuccess, {
          variant: 'success',
        });
        datascopeTracker('Save Email Configuration', { source: 'FORM_BUILDER', formId }, 'AMP');
      })
      .catch(() => {
        enqueueSnackbar(language.formEmailBuilder.saveError, {
          variant: 'error',
        });
      });
  };

  useEffect(() => {
    async function fetchData() {
      const {
        email_subject,
        email_sender_name,
        email_content,
        email_footer,
        auto_notify,
        emails_to_notify,
        email_button_name,
      } = await taskFormsApi.getTaskFormEmailInfo(parseInt(formId));
      email_subject && setSubject(email_subject);
      email_sender_name && setSenderName(email_sender_name);
      email_content && setEmailContent(email_content);
      email_footer && setEmailFooter(email_footer);
      auto_notify && setAutoNotify(auto_notify);
      emails_to_notify && setEmailsToNotify(emails_to_notify.trim().split(','));
      email_button_name && setLinkName(email_button_name);
      if (email_footer) setEnableFooter(true);
    }
    fetchData();
  }, []);

  return (
    <>
      <Paper
        elevation={1}
        sx={customStyles.paper}
      >
        <Typography
          variant='h1'
          component='h2'
        >
          {language.formEmailBuilder.message}
        </Typography>
        <Box>
          <Box sx={customStyles.senderAndSubjectContainer}>
            <Box sx={customStyles.textInputDiv}>
              <Typography variant='body1'>{language.formEmailBuilder.subject}</Typography>
              <TextField
                size='small'
                variant='outlined'
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                sx={customStyles.textInput}
              />
            </Box>
            <Box sx={customStyles.textInputDiv}>
              <Typography variant='body1'>{language.formEmailBuilder.senderName}</Typography>
              <TextField
                size='small'
                variant='outlined'
                value={senderName}
                onChange={(event) => setSenderName(event.target.value)}
                sx={customStyles.textInput}
              />
            </Box>
          </Box>
          <Box sx={customStyles.emailContentContainer}>
            <Ckeditor
              value={emailContent}
              onChange={(value: string) => setEmailContent(value)}
            />
          </Box>
          <FormControlLabel
            sx={customStyles.checkboxWithLabel}
            control={<Checkbox checked={enableFooter} />}
            onChange={() => setEnableFooter(!enableFooter)}
            label={language.formEmailBuilder.addFooter}
          />
          {enableFooter && (
            <>
              <Typography
                variant='body1'
                sx={customStyles.footerTitle}
              >
                {language.formEmailBuilder.footerTitle}
              </Typography>
              <Ckeditor
                value={emailFooter}
                onChange={(value: string) => setEmailFooter(value)}
              />
            </>
          )}
        </Box>
        <Divider sx={customStyles.divider} />
        <Box>
          <Typography
            variant='h1'
            component='h2'
          >
            {language.formEmailBuilder.notification}
          </Typography>
          <Box sx={customStyles.notificationsConfigContainer}>
            <FormControlLabel
              sx={customStyles.checkboxWithLabel}
              control={<Checkbox checked={autoNotify} />}
              onChange={() => setAutoNotify(!autoNotify)}
              label={language.formEmailBuilder.autoNotify}
            />
            {autoNotify && (
              <MultiEmailInput
                hideLabel
                emails={emailsToNotify}
                onEmailChange={(emails) => setEmailsToNotify(emails)}
              />
            )}
          </Box>
          <Box sx={customStyles.textInputDiv}>
            <Typography
              variant='body1'
              sx={customStyles.pdfLinkNameTitle}
            >
              {language.formEmailBuilder.linkName}
              <Tooltip
                arrow
                title={language.formEmailBuilder.linkNameHelp}
                placement={'right'}
              >
                <InfoOutlinedIcon
                  fontSize='small'
                  sx={customStyles.helpIcon}
                />
              </Tooltip>
            </Typography>
            <TextField
              size='small'
              variant='outlined'
              value={linkName}
              onChange={(event) => setLinkName(event.target.value)}
              placeholder={language.formEmailBuilder.linkNamePlaceholder}
              sx={customStyles.textInput}
            />
          </Box>
        </Box>
      </Paper>
      <FormEmailBuilderBar onConfirm={handleConfirm} />
    </>
  );
}
